/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@eliadmin/core/src/components/ui/card';
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  useDialogStore,
} from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuSearch } from 'react-icons/lu';

export type PlaceOfDeliveryLocal = {
  address: string;
  street_number: string;
  postcode: string;
  city: string;
  id: number;
  identifier: string;
  latitude: string;
  longitude: string;
  name: string;
  opening_hours: string;
};
interface CartShipmentPlaceDialogProps {
  pickPlace: (point: any) => void;
  place_of_deliveries?: PlaceOfDeliveryLocal[];
}

export function normalizeText(text: string) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function CartShipmentPlaceDialog(props: CartShipmentPlaceDialogProps) {
  const close = useDialogStore(s => s.close);

  const [highlightedPlace, setHighlightedPlace] = useState<number | string | null>(null);

  const { 'google-api': googleApiKey } = useGlobalStore(state => state.apiKeys);

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey?.token ?? '',
  });

  const { t } = useTranslations();

  const listRef = useRef<HTMLDivElement>(null);

  const [zoom, setZoom] = useState(10);

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onUnmount = useCallback(
    function callback(/*map: google.maps.Map*/) {
      setMap(null);
    },
    [],
  );

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    map.setZoom(zoom);
    map.setOptions({
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
    });

    setMap(map);
  }, []);

  const [center, setCenter] = useState({
    lat: props.place_of_deliveries?.[0]?.latitude
      ? +props.place_of_deliveries?.[0]?.latitude
      : 48.13881165313404,
    lng: props.place_of_deliveries?.[0]?.longitude
      ? +props.place_of_deliveries?.[0]?.longitude
      : 17.118919055143596,
  });

  const handleClick = (place: any) => {
    props.pickPlace(place);
    close(CartShipmentPlaceDialog.id);
  };

  const filteredPlaces = useMemo(() => {
    const newPlaces = props.place_of_deliveries?.filter(point => {
      return normalizeText(
        point.name + point.address + point.street_number + point.city + point.postcode,
      ).includes(normalizeText(deferredQuery));
    });

    return !highlightedPlace
      ? newPlaces
      : newPlaces?.sort((a, b) => {
          if (a.id === highlightedPlace) return -1;
          if (b.id === highlightedPlace) return 1;
          return 0;
        });
  }, [deferredQuery, highlightedPlace, props.place_of_deliveries]);

  useEffect(() => {
    if (!highlightedPlace || filteredPlaces?.some(point => point.id === highlightedPlace)) {
      return;
    }

    const nextHighlightedPlace = filteredPlaces?.[0];
    if (!nextHighlightedPlace) {
      setHighlightedPlace(null);
      return;
    }

    setHighlightedPlace(nextHighlightedPlace.id);
    setCenter({
      lat: +nextHighlightedPlace.latitude,
      lng: +nextHighlightedPlace.longitude,
    });
  }, [filteredPlaces, highlightedPlace]);

  // scroll to highlighted place
  useEffect(() => {
    if (highlightedPlace && listRef.current) {
      const highlightedElement = listRef.current.querySelector(
        `[data-delivery-point-id="${highlightedPlace}"]`,
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
      }
    }
  }, [highlightedPlace]);

  return (
    <DialogContent className="hide-scroll-bar flex h-full min-h-[400px] flex-col overflow-auto sm:w-11/12 sm:max-w-screen-xl md:w-11/12">
      <DialogHeader className={cn('', 'ea-cart-shipment-place-dialog__header')}>
        <DialogTitle className={cn('font-semibold', 'ea-cart-shipment-place-dialog__title')}>
          {t('text440')}
        </DialogTitle>
      </DialogHeader>
      <div className="grid h-full grid-cols-12 grid-rows-2 md:grid-rows-1 md:gap-1">
        <div className="col-span-12 flex flex-col gap-3 md:col-span-4 ">
          <Input
            before={<LuSearch />}
            placeholder={t('text162')}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div
            ref={listRef}
            className="hide-scroll-bar flex h-full max-h-[300px] flex-col gap-1 overflow-scroll md:max-h-none"
          >
            {filteredPlaces?.map(point => (
              <Card
                key={point.id}
                className={cn(
                  'flex flex-col',
                  highlightedPlace === point.id ? 'border-primary bg-input' : 'bg-white',
                )}
                elevation={'xl'}
                variant={'outlined'}
                data-delivery-point-id={point.id}
                onClick={() => {
                  if (highlightedPlace !== point.id) setHighlightedPlace(point.id);
                  else setHighlightedPlace(null);
                }}
              >
                <CardHeader
                  className="cursor-pointer p-4"
                  onClick={() => {
                    setCenter({
                      lat: +point.latitude,
                      lng: +point.longitude,
                    });
                    if (highlightedPlace !== point.id) setHighlightedPlace(point.id);
                    else setHighlightedPlace(null);
                  }}
                >
                  <div className="flex w-full justify-between">
                    <div>
                      {point.address} {point.street_number}, {point.city} {point.postcode}
                    </div>
                    <LuChevronDown
                      className={cn('h-6 w-6', highlightedPlace === point.id && 'rotate-180')}
                    />
                  </div>
                  <CardTitle>{point.name}</CardTitle>
                </CardHeader>

                {/* Collapsible content */}
                {highlightedPlace === point.id && (
                  <>
                    <CardContent
                      className="flex cursor-pointer flex-col p-4 pt-0"
                      onClick={() => {
                        setCenter({
                          lat: +point.latitude,
                          lng: +point.longitude,
                        });
                        if (highlightedPlace !== point.id) setHighlightedPlace(point.id);
                        else setHighlightedPlace(null);
                      }}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-col text-sm">
                          <span className="font-semibold">
                            <pre>{point.opening_hours}</pre>
                          </span>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="p-4 pt-0">
                      <Button className="w-full" size="xs" onClick={() => handleClick(point)}>
                        {t('text440')}
                      </Button>
                    </CardFooter>
                  </>
                )}
              </Card>
            ))}
          </div>
        </div>
        <div className="hide-scroll-bar col-span-12 flex h-full md:col-span-8">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '100%',
              }}
              zoom={zoom}
              center={center}
              onLoad={onLoad}
              onUnmount={onUnmount}
              clickableIcons={false}
            >
              {filteredPlaces?.map(point => (
                <Marker
                  key={point.id}
                  position={{
                    lat: +point.latitude,
                    lng: +point.longitude,
                  }}
                  opacity={highlightedPlace === null ? 1 : highlightedPlace === point.id ? 1 : 0.5}
                  onClick={() => {
                    setCenter({
                      lat: +point.latitude,
                      lng: +point.longitude,
                    });
                    setZoom(13);
                    if (highlightedPlace !== point.id) setHighlightedPlace(point.id);
                    else setHighlightedPlace(null);
                  }}
                />
              ))}
            </GoogleMap>
          ) : null}
        </div>
      </div>
    </DialogContent>
  );
}

CartShipmentPlaceDialog.id = 'PICK_SHIPMENT_PLACE_DIALOG';
